import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { Loader2, Lock, Crown, CheckCircle2, XCircle, RotateCcw, ArrowLeft, ArrowRight, Trophy } from 'lucide-react';
import api from '../lib/api';
import { useLang, tx } from '../i18n/LanguageContext';
import { usePageMeta } from '../lib/seo';

const pick = (lang, ar, en) => (lang === 'ar' ? ar : (en || ar));

const LockedQuiz = ({ L }) => (
  <div data-testid="quiz-locked" className="max-w-lg mx-auto text-center bg-white rounded-3xl border border-amber-200 p-8 shadow-sm">
    <span className="w-16 h-16 mx-auto rounded-2xl bg-amber-400 flex items-center justify-center mb-5"><Lock className="w-8 h-8 text-slate-900" /></span>
    <h2 className="text-2xl font-black text-slate-900 mb-2">{L('هذا الاختبار للمشتركين', 'This quiz is for subscribers')}</h2>
    <p className="text-slate-500 mb-7">{L('اشترك للوصول إلى جميع الاختبارات والكورسات', 'Subscribe to unlock every quiz and course')}</p>
    <Link to="/subscription" data-testid="quiz-locked-subscribe" className="btn-subscribe h-12 px-7"><Crown className="w-5 h-5" />{L('عرض الباقات', 'View plans')}</Link>
  </div>
);

const Results = ({ quiz, result, answers, lang, L, onRetry }) => (
  <div data-testid="quiz-results" className="space-y-6">
    <div className="text-center bg-white rounded-3xl border border-slate-200 p-6 sm:p-8 shadow-sm">
      <span className={`w-20 h-20 mx-auto rounded-full flex items-center justify-center mb-4 ${result.percent >= 50 ? 'bg-emerald-100 text-emerald-600' : 'bg-rose-100 text-rose-500'}`}><Trophy className="w-10 h-10" /></span>
      <div data-testid="quiz-score" className="text-4xl font-black text-slate-900 mb-1" dir="ltr">{result.percent}%</div>
      <p className="text-slate-500 mb-6">{L('أجبت بشكل صحيح على', 'You answered correctly')} {result.score} / {result.total}</p>
      <button data-testid="quiz-retry" onClick={onRetry} className="inline-flex items-center gap-2 h-12 px-7 rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white font-bold transition-colors"><RotateCcw className="w-4 h-4" />{L('أعد الاختبار', 'Retry quiz')}</button>
    </div>
    {quiz.questions.map((q, i) => {
      const r = result.results[i];
      return (
        <div key={q.id} data-testid={`quiz-review-${i}`} className={`bg-white rounded-2xl border p-5 sm:p-6 ${r.is_correct ? 'border-emerald-200' : 'border-rose-200'}`}>
          <div className="flex items-start gap-3 mb-4">
            {r.is_correct ? <CheckCircle2 className="w-6 h-6 text-emerald-500 shrink-0" /> : <XCircle className="w-6 h-6 text-rose-500 shrink-0" />}
            <h3 className="font-bold text-slate-900">{i + 1}. {pick(lang, q.textAr, q.textEn)}</h3>
          </div>
          <div className="space-y-2">
            {q.options.map((o, j) => (
              <div key={j} className={`px-4 py-2.5 rounded-xl text-sm border ${j === r.correct ? 'bg-emerald-50 border-emerald-300 text-emerald-800 font-bold' : j === answers[i] ? 'bg-rose-50 border-rose-300 text-rose-700' : 'border-slate-100 text-slate-600'}`}>{pick(lang, o.ar, o.en)}</div>
            ))}
          </div>
          {pick(lang, r.explainAr, r.explainEn) && <p className="mt-4 text-sm text-slate-600 bg-slate-50 rounded-xl p-3">{pick(lang, r.explainAr, r.explainEn)}</p>}
        </div>
      );
    })}
  </div>
);

export default function QuizDetail() {
  const { id } = useParams();
  const { lang, isRTL } = useLang();
  const L = (ar, en) => tx(lang, ar, en);
  const [quiz, setQuiz] = useState(null);
  const [step, setStep] = useState(0);
  const [answers, setAnswers] = useState([]);
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(false);
  const Next = isRTL ? ArrowLeft : ArrowRight;
  const Prev = isRTL ? ArrowRight : ArrowLeft;

  useEffect(() => { api.get(`/quizzes/${id}`).then(({ data }) => setQuiz(data)).catch(() => setError(true)); }, [id]);
  usePageMeta(quiz && pick(lang, quiz.titleAr, quiz.titleEn), quiz && pick(lang, quiz.descAr, quiz.descEn));

  const retry = () => { setAnswers([]); setStep(0); setResult(null); window.scrollTo({ top: 0 }); };
  const submit = async () => {
    setBusy(true);
    try { const { data } = await api.post(`/quizzes/${id}/submit`, { answers }); setResult(data); window.scrollTo({ top: 0 }); }
    finally { setBusy(false); }
  };

  if (error) return <main className="pt-28 min-h-screen text-center text-slate-500">{L('الاختبار غير موجود', 'Quiz not found')}</main>;
  if (!quiz) return <main className="pt-28 min-h-screen flex justify-center"><Loader2 className="w-8 h-8 animate-spin text-blue-600" /></main>;

  const questions = quiz.questions || [];
  const q = questions[step];
  return (
    <main data-testid="quiz-detail-page" className="page-y min-h-screen">
      <div className="max-w-3xl mx-auto px-4 sm:px-6">
        <Link to="/quizzes" data-testid="quiz-back" className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-blue-600 mb-5"><Prev className="w-4 h-4" />{L('كل الاختبارات', 'All quizzes')}</Link>
        <h1 className="text-3xl sm:text-4xl font-black text-slate-900 mb-2">{pick(lang, quiz.titleAr, quiz.titleEn)}</h1>
        <p className="text-slate-500 mb-8">{pick(lang, quiz.descAr, quiz.descEn)}</p>
        {quiz.locked ? <LockedQuiz L={L} /> : result ? (
          <Results quiz={quiz} result={result} answers={answers} lang={lang} L={L} onRetry={retry} />
        ) : !q ? (
          <div className="text-center text-slate-500 py-10">{L('لا توجد أسئلة بعد', 'No questions yet')}</div>
        ) : (
          <div className="bg-white rounded-3xl border border-slate-200 p-6 sm:p-8 shadow-sm">
            <div className="flex items-center justify-between text-sm text-slate-500 mb-3">
              <span data-testid="quiz-progress">{L('السؤال', 'Question')} {step + 1} / {questions.length}</span>
            </div>
            <div className="h-2 rounded-full bg-slate-100 mb-7 overflow-hidden"><div className="h-full bg-emerald-500 rounded-full transition-[width] duration-300" style={{ width: `${((step + 1) / questions.length) * 100}%` }} /></div>
            <h2 data-testid="quiz-question" className="text-xl sm:text-2xl font-extrabold text-slate-900 mb-6">{pick(lang, q.textAr, q.textEn)}</h2>
            <div className="space-y-3 mb-8">
              {q.options.map((o, j) => {
                const sel = answers[step] === j;
                return (
                  <button key={j} type="button" data-testid={`quiz-option-${j}`} onClick={() => setAnswers(a => { const n = [...a]; n[step] = j; return n; })}
                    className={`w-full flex items-center gap-3 text-start px-5 py-4 rounded-2xl border-2 transition-[border-color,background-color] ${sel ? 'border-emerald-500 bg-emerald-50' : 'border-slate-200 hover:border-slate-300 hover:bg-slate-50'}`}>
                    <span className={`w-8 h-8 shrink-0 rounded-lg flex items-center justify-center text-sm font-bold ${sel ? 'bg-emerald-500 text-white' : 'bg-slate-100 text-slate-600'}`}>{String.fromCharCode(65 + j)}</span>
                    <span className="font-medium text-slate-800">{pick(lang, o.ar, o.en)}</span>
                  </button>
                );
              })}
            </div>
            <div className="flex items-center justify-between gap-3">
              <button data-testid="quiz-prev" disabled={step === 0} onClick={() => setStep(s => s - 1)} className="inline-flex items-center gap-1.5 h-12 px-5 rounded-xl border border-slate-200 text-slate-700 font-bold disabled:opacity-40 hover:bg-slate-50 transition-colors"><Prev className="w-4 h-4" />{L('السابق', 'Previous')}</button>
              {step < questions.length - 1 ? (
                <button data-testid="quiz-next" disabled={answers[step] === undefined} onClick={() => setStep(s => s + 1)} className="inline-flex items-center gap-1.5 h-12 px-6 rounded-xl bg-blue-600 hover:bg-blue-700 text-white font-bold disabled:opacity-40 transition-colors">{L('التالي', 'Next')}<Next className="w-4 h-4" /></button>
              ) : (
                <button data-testid="quiz-submit" disabled={answers[step] === undefined || busy} onClick={submit} className="inline-flex items-center gap-2 h-12 px-6 rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white font-bold disabled:opacity-40 transition-colors">{busy && <Loader2 className="w-4 h-4 animate-spin" />}{L('إنهاء وعرض النتيجة', 'Finish & see result')}</button>
              )}
            </div>
          </div>
        )}
      </div>
    </main>
  );
}
